import { useEffect, useState } from 'react';
import { recruiterApi } from '@/services/recruiterApi';

// Module-level cache mirroring useLiveTvEnabled: probe /api/recruiter/enabled
// once per page load and share the result across the nav tab, the route guard,
// and the pages. `undefined` = not yet probed.
let cached: boolean | undefined;
let inflight: Promise<boolean> | null = null;

async function probe(): Promise<boolean> {
  if (cached !== undefined) return cached;
  if (inflight) return inflight;
  inflight = recruiterApi
    .enabled()
    .then((r) => {
      cached = r.enabled === true;
      return cached;
    })
    .catch(() => {
      cached = false;
      return false;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/**
 * True once the backend confirms the recruiter platform is enabled. Probes only
 * when `active` (the user has access / is admin) so guests don't fire a doomed
 * request. Pair with useEntitlements().isRecruiter for nav visibility.
 */
export function useRecruiterEnabled(active: boolean): boolean {
  const [enabled, setEnabled] = useState<boolean>(cached ?? false);
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    void probe().then((v) => {
      if (!cancelled) setEnabled(v);
    });
    return () => {
      cancelled = true;
    };
  }, [active]);
  return enabled;
}

/** Test-only: reset the module cache between cases. */
export function __resetRecruiterEnabledCache(): void {
  cached = undefined;
  inflight = null;
}

/** Test-only: seed the cache so the hook resolves without firing a probe. */
export function __primeRecruiterEnabledCache(value: boolean): void {
  cached = value;
  inflight = null;
}
